import { ContentType, QBankStructureLevelCategory } from '@exam-management/graphql';
import { findDescendants, useQBankStructure } from '@exam-management/qbs-provider';
import {
  type GridColDef,
  type GridRenderCellParams,
  DataGrid,
  Flex,
  styled,
  Text,
  VisibilityOffOutlinedIcon,
} from '@shared/components';
import { useEffect, useMemo } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import type { QBankStructureCVRaw } from '../types';
import { transformQBankStructureLevelItemsToCV } from '../utils';

import { FormQBankStructureCVChildCheckbox } from './form-qbank-structure-cv-child-checkbox';
import { FormQBankStructureCVParentCheckbox } from './form-qbank-structure-cv-parent-checkbox';
import { QBankStructureCVHeader } from './qbank-structure-cv-header';

export const levelItemsToContentType: Record<QBankStructureLevelCategory, ContentType> = {
  COURSE: ContentType.QbankStructureLevelCourse,
  PART: ContentType.QbankStructureLevelPart,
  SUBJECT: ContentType.QbankStructureLevelSubject,
  CHAPTER: ContentType.QbankStructureLevelChapter,
  GROUP: ContentType.QbankStructureLevelGroup,
  TOPIC: ContentType.QbankStructureLevelTopic,
  SUB_TOPIC: ContentType.QbankStructureLevelSubTopic,
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
    '.MuiDataGrid-columnSeparator:last-of-type': {
      display: 'none',
    },
    '.MuiDataGrid-columnHeader--withRightBorder': {
      border: 'none',
    },
  },
  '& .MuiDataGrid-row': {
    [`&.row-${QBankStructureLevelCategory.Part}`]: {
      '&.variant-only-part, &:hover.variant-only-part': {
        backgroundColor: theme.palette.common.white,
        '&:hover': {
          backgroundColor: `${theme.palette.grey[100]}!important`,
        },
      },
      '&.variant-both, &:hover.variant-both': {
        backgroundColor: theme.palette.primary.light,
        '&:hover': {
          backgroundColor: `${theme.palette.primary.lighter}!important`,
        },
      },
    },
    '> .MuiDataGrid-cell:last-child': {
      borderRight: 'none',
    },
  },
  '& .MuiDataGrid-withBorderColor': {
    borderColor: theme.palette.grey[300],
  },
  '.MuiDataGrid-cell': {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2.75),
  },
  '.MuiDataGrid-columnHeader': {
    padding: theme.spacing(3),
  },
  [`& .row-${QBankStructureLevelCategory.Subject} > .MuiDataGrid-cell`]: {
    overflow: 'auto !important',
  },
  [`& .row-${QBankStructureLevelCategory.Subject} > .MuiDataGrid-cell:last-child`]: {
    overflow: 'hidden !important',
  },
  [`& .row-${QBankStructureLevelCategory.Subject}.variant-both > .MuiDataGrid-cell > p`]: {
    paddingLeft: theme.spacing(10),
  },
}));

interface QBankStructureCVListProps {
  rows: QBankStructureCVRaw[];
  disabled?: boolean;
}

function QBankStructureCVList(props: Readonly<QBankStructureCVListProps>) {
  const { rows, disabled: isDisabled = false } = props;

  const formContext = useFormContext();

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Content Structure',
      headerAlign: 'center',
      flex: 8,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<QBankStructureCVRaw>) => {
        return (
          <Text variant='body2' sx={(theme) => ({ fontWeight: theme.typography.body2Semibold })}>
            {params.row.qBankStructureLevel.category === QBankStructureLevelCategory.Part
              ? `หมวดที่ ${params.row.order}`
              : `เล่ม/วิชาที่ ${params.row.order}`}
            &nbsp;
            {params.row.tag?.name}
          </Text>
        );
      },
    },
    {
      field: 'isVisible',
      headerName: 'Allow Access',
      headerAlign: 'center',
      flex: 1,
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<QBankStructureCVRaw>) => {
        switch (params.row.qBankStructureLevel.category) {
          case QBankStructureLevelCategory.Part:
            return <FormQBankStructureCVParentCheckbox {...params} disabled={isDisabled} />;
          case QBankStructureLevelCategory.Subject:
            return <FormQBankStructureCVChildCheckbox {...params} disabled={isDisabled} />;
          default:
            return null;
        }
      },
    },
  ];

  const handleChange = (parentId: string, isChecked: boolean) => {
    const descendants = findDescendants(rows, parentId);

    for (const child of descendants) {
      formContext.setValue(`contentIds.${child.id}.isVisible`, isChecked);
    }
    formContext.setValue(`contentIds.${parentId}.isVisible`, isChecked);
  };

  useEffect(() => {
    const formData = { ...formContext.getValues('contentIds') };
    for (const row of rows) {
      formData[row.id] = {
        isVisible: formData[row.id]?.isVisible ?? false,
        type: row.qBankStructureLevel.category,
        parentLevelItemId: row.parentLevelItemId,
      };
    }

    formContext.setValue('contentIds', formData, { shouldValidate: true });
  }, [rows]);

  const hasPart = useMemo(
    () =>
      rows.some(
        ({ qBankStructureLevel }) =>
          qBankStructureLevel.category === QBankStructureLevelCategory.Part,
      ),
    [rows],
  );
  const hasSubject = useMemo(
    () =>
      rows.some(
        ({ qBankStructureLevel }) =>
          qBankStructureLevel.category === QBankStructureLevelCategory.Subject,
      ),
    [rows],
  );

  const variant = useMemo(() => {
    if (hasPart && hasSubject) return 'both';
    if (hasPart) return 'only-part';
    if (hasSubject) return 'only-subject';
    return '';
  }, [hasPart, hasSubject]);

  const displayRows = useMemo(() => {
    return rows.filter(
      (row) =>
        row.qBankStructureLevel.category === QBankStructureLevelCategory.Part ||
        row.qBankStructureLevel.category === QBankStructureLevelCategory.Subject,
    );
  }, [rows]);

  if (displayRows.length === 0) {
    return (
      <>
        <QBankStructureCVHeader selectAllProps={{ hidden: true }} />
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
            py: theme.spacing(16),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.shape.rounded(2),
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          })}
        >
          <VisibilityOffOutlinedIcon
            sx={(theme) => ({ color: theme.palette.grey[400], fontSize: '100px' })}
          />
          <Flex sx={{ flexDirection: 'column' }}>
            <Text variant='h3'>โครงสร้าง (Structure) ข้อมูลที่เข้าถึงไม่เปิดใช้งาน</Text>
            <Text variant='body2' sx={(theme) => ({ color: theme.palette.text.secondary })}>
              เนื่องจากระดับโครงสร้าง LV2 : หมวด (Part) และ LV3 : เล่ม/วิชา (Subject) ไม่เปิดใช้งาน
              <br />
              จึงไม่มีข้อมูลให้ตั้งค่าการมองเห็นในขณะนี้
            </Text>
          </Flex>
        </Flex>
      </>
    );
  }

  return (
    <>
      <QBankStructureCVHeader selectAllProps={{ disabled: isDisabled }} />
      <StyledDataGrid
        columns={columns}
        rows={displayRows}
        getRowClassName={(row) =>
          `row-${row.row.qBankStructureLevel.category} ${hasPart && `variant-${variant}`} `
        }
        getRowHeight={() => 48}
        getRowId={(row) => row.id}
        showCellVerticalBorder
        showColumnVerticalBorder
        hideFooter
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableEval
        onRowClick={(params) => {
          if (isDisabled) return;
          handleChange(params.row.id, !formContext.watch(`contentIds.${params.row.id}.isVisible`));
        }}
      />
    </>
  );
}

interface PrepareQBankStructureCVListProps {
  disabled?: boolean;
}

function PrepareQBankStructureCVList(props: Readonly<PrepareQBankStructureCVListProps>) {
  const { disabled: isDisabled = false } = props;

  const { qBankStructureLevelItems } = useQBankStructure();

  const rows = useMemo(() => {
    return transformQBankStructureLevelItemsToCV(qBankStructureLevelItems);
  }, [qBankStructureLevelItems]);

  const formContext = useFormContext();

  return (
    <FormProvider {...formContext}>
      <QBankStructureCVList
        rows={rows}
        disabled={isDisabled || formContext.formState.isSubmitting}
      />
    </FormProvider>
  );
}

export { PrepareQBankStructureCVList as QBankStructureCVList };
